/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Grid, Typography, Typography2, breakpoints, useTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import type {
  EntWebsiteBlockUpcomingEventsQuery,
  EntWebsiteUpcomingEventsFragment,
} from 'bundles/ent-website/__generated__/contentfulTypes';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import BlockLayout, { noImage } from 'bundles/ent-website/contentful/core/BlockLayout';
import ContentfulButton from 'bundles/ent-website/contentful/core/ContentfulButton';
import { entWebsiteBlockUpcomingEventsQuery } from 'bundles/ent-website/queries/EntWebsiteUpcomingEvents';
import { compactBlockStyle } from 'bundles/ent-website/shared-styles';
import type { BlockModuleInnerProps } from 'bundles/ent-website/utils/withBlockQuery';
import withBlockQuery from 'bundles/ent-website/utils/withBlockQuery';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';

import _t from 'i18n!nls/ent-website';

export type Props = BlockModuleInnerProps<EntWebsiteUpcomingEventsFragment>;

const styles = {
  headerCnt: () => css`
    margin-bottom: var(--cds-spacing-300);
    padding-bottom: var(--cds-spacing-300);
    border-bottom: 1px solid var(--cds-color-grey-200);

    ${breakpoints.down('sm')} {
      margin-bottom: var(--cds-spacing-400);
      padding-bottom: var(--cds-spacing-300);
    }
  `,
  headerTitle: () => css`
    line-height: 1;
    font-size: 32px;
    font-weight: 600;
    letter-spacing: -0.96px;

    ${breakpoints.down('sm')} {
      font-size: var(--cds-spacing-300);
    }
  `,
  headerCta: () => css`
    ${breakpoints.down('sm')} {
      display: none;
    }
  `,
  upcomingEventCards: () => css`
    display: flex;
    flex-direction: row;

    ${breakpoints.down('sm')} {
      flex-direction: column;
    }
  `,
  upcomingEventCardWrapper: () => css`
    cursor: pointer;
    display: flex;
    flex-direction: row;
    width: 100%;
    margin-top: var(--cds-spacing-300);
    padding-right: var(--cds-spacing-400);
    text-decoration: none !important;

    :hover {
      button {
        text-decoration: underline;
      }
    }

    ${breakpoints.down('sm')} {
      margin: 0;
      margin-bottom: var(--cds-spacing-400);
    }
  `,
  upcomingEventCardImage: (_: Theme, imageUrl: string) => css`
    height: 100%;
    width: 175px;
    aspect-ratio: 1 / 1;
    border-radius: 8px;
    overflow: hidden;
    background-image: url('${imageUrl}');
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;

    ${breakpoints.down('sm')} {
      width: 120px;
      height: 120px;
    }
  `,
  upcomingEventCardBody: () => css`
    flex: 1;
    flex-wrap: nowrap;
    padding: var(--cds-spacing-150) 0 var(--cds-spacing-150) var(--cds-spacing-200);

    ${breakpoints.down('sm')} {
      padding: 0 0 0 var(--cds-spacing-200);
    }
  `,
  upcomingEventCardEyebrow: css`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: var(--cds-color-neutral-primary-weak);
    font-size: 14px;
    font-weight: 700;
    line-height: 140%;
    min-height: 1rem;
  `,
  upcomingEventCardHeadlineWrapper: css`
    flex: 1;
  `,
  upcomingEventCardHeadline: () => css`
    display: flex;
    flex-grow: 1;
    /* stylelint-disable-next-line declaration-block-no-duplicate-properties */
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    margin: var(--cds-spacing-100) 0;
    color: var(--cds-color-grey-975);
    font-size: 22px;
    font-weight: 600;
    line-height: 26.4px;
    max-height: 79.2px;
    letter-spacing: -0.44px;

    ${breakpoints.down('sm')} {
      -webkit-line-clamp: 2;
      line-height: 26.4px;
      max-height: 52.8px;
      font-size: 16px;
      margin-bottom: var(--cds-spacing-100);
    }
  `,
  upcomingEventCardCta: css`
    margin-left: -8px;
    background: none !important;
  `,
  footerCnt: () => css`
    padding-top: var(--cds-spacing-150);
    border-top: 1px solid var(--cds-color-grey-200);

    ${breakpoints.up('md')} {
      display: none;
    }
  `,
  footerCta: css`
    margin-left: -16px;
    background: none !important;
  `,
};

export function UpcomingEventsModule({ content, assembly }: Props) {
  const theme = useTheme();

  const contentPanel = () => (
    <Grid container direction="column">
      <Grid container justifyContent="space-between" alignItems="center" css={styles.headerCnt}>
        <Typography variant="d2semibold" css={styles.headerTitle}>
          {content?.headerTitle}
        </Typography>
        {content?.headerCta?.ctaText && content?.headerCta?.ctaTarget && (
          <ContentfulButton
            styles={styles.headerCta}
            variant="ghost"
            component="a"
            href={content?.headerCta?.ctaTarget}
            position={0}
            module={content}
            icon={<ArrowNextIcon />}
            target={content?.headerCta?.shouldOpenInNewWindow ? '_blank' : undefined}
          >
            {content?.headerCta?.ctaText}
          </ContentfulButton>
        )}
      </Grid>
      {content?.upcomingEventsCollection?.items && content?.upcomingEventsCollection?.items?.length > 0 && (
        <Grid container css={styles.upcomingEventCards}>
          {content?.upcomingEventsCollection?.items?.map(
            (upcomingEvent) =>
              upcomingEvent?.image?.url &&
              upcomingEvent?.cta?.ctaTarget && (
                <Grid
                  component={TrackedLink2}
                  trackingName="upcoming_event"
                  item
                  sm={12}
                  md={6}
                  lg={4}
                  key={upcomingEvent?.sys?.id}
                  css={styles.upcomingEventCardWrapper}
                  href={upcomingEvent?.cta?.ctaTarget}
                  target={upcomingEvent?.cta?.shouldOpenInNewWindow ? '_blank' : undefined}
                  rel="noreferrer"
                >
                  <div css={styles.upcomingEventCardImage(theme, upcomingEvent?.image?.url)} />
                  <Grid container alignItems="flex-start" direction="column" css={styles.upcomingEventCardBody}>
                    <Typography2 component="p" css={styles.upcomingEventCardEyebrow}>
                      {upcomingEvent?.eyebrow}
                    </Typography2>
                    <Grid container css={styles.upcomingEventCardHeadlineWrapper}>
                      <Typography2 component="p" css={styles.upcomingEventCardHeadline}>
                        {upcomingEvent?.headline}
                      </Typography2>
                    </Grid>
                    {upcomingEvent?.cta?.ctaTarget && upcomingEvent?.cta?.ctaText && (
                      <Button
                        variant="ghost"
                        icon={<ArrowNextIcon />}
                        iconPosition="after"
                        size="small"
                        css={styles.upcomingEventCardCta}
                      >
                        {upcomingEvent?.cta?.ctaText ?? _t('Learn More')}
                      </Button>
                    )}
                  </Grid>
                </Grid>
              )
          )}
        </Grid>
      )}
      {content?.headerCta?.ctaText && content?.headerCta?.ctaTarget && (
        <Grid container justifyContent="space-between" alignItems="center" css={styles.footerCnt}>
          <ContentfulButton
            variant="ghost"
            component="a"
            href={content?.headerCta?.ctaTarget}
            position={0}
            module={content}
            icon={<ArrowNextIcon />}
            target={content?.headerCta?.shouldOpenInNewWindow ? '_blank' : undefined}
            styles={styles.footerCta}
          >
            {content?.headerCta?.ctaText}
          </ContentfulButton>
        </Grid>
      )}
    </Grid>
  );

  return <BlockLayout blockStyle={compactBlockStyle} assembly={noImage(assembly)} renderContent={contentPanel} />;
}

export const BlockUpcomingEventsModule = withBlockQuery<
  EntWebsiteUpcomingEventsFragment,
  // @ts-expect-error TSMIGRATION-5.0 TS2344
  EntWebsiteBlockUpcomingEventsQuery
>(entWebsiteBlockUpcomingEventsQuery)(UpcomingEventsModule);
